'use client';

import type { Namespace } from 'i18next';
import type { LangCode } from './locales-config';

import dayjs from 'dayjs';
import { useEffect, useCallback } from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import { useTranslation, Trans as I18Trans } from 'react-i18next';

import { toast } from '@/components/snackbar';

import { useRouter } from '@/routes/hooks';

import { fallbackLng, getCurrentLang } from './locales-config';

export function useTranslate(namespace?: Namespace) {
  const router = useRouter();

  const { t, i18n } = useTranslation(namespace);
  const { t: tMessages } = useTranslation('messages');

  const currentLang = getCurrentLang(i18n.resolvedLanguage);

  const updateDayjsLocale = useCallback((lang: LangCode) => {
    const updatedLang = getCurrentLang(lang);
    dayjs.locale(updatedLang.adapterLocale);
  }, []);

  const handleChangeLang = useCallback(
    async (lang: LangCode) => {
      try {
        // Only proceed if the language is actually changing
        if (i18n.resolvedLanguage !== lang) {
          const changeLangPromise = i18n.changeLanguage(lang);

          toast.promise(changeLangPromise, {
            loading: tMessages('languageSwitch.loading'),
            success: () => tMessages('languageSwitch.success'),
            error: () => tMessages('languageSwitch.error'),
          });

          await changeLangPromise;
          updateDayjsLocale(lang);

          // Only refresh if we're in a browser environment
          if (typeof window !== 'undefined') {
            router.refresh(); // only nextjs
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [i18n, router, tMessages, updateDayjsLocale]
  );

  const handleResetLang = useCallback(() => {
    handleChangeLang(fallbackLng);
  }, [handleChangeLang]);

  const tSmart = useCallback(
    (key: string, options: Record<string, any> = {}) => {
      const raw = t(key, { ...options, returnObjects: false });

      const isICU = /{[^}]+,[^}]+}/.test(raw); // check for ICU syntax

      if (isICU) {
        try {
          const msg = new IntlMessageFormat(raw, i18n.language);
          return msg.format(options);
        } catch (err) {
          console.warn('ICU parse error:', err);
          return raw;
        }
      }

      return raw;
    },
    [t, i18n.language]
  );

  const Trans = ({ ...props }: any) => <I18Trans ns={namespace} {...props} />;

  return {
    t,
    tSmart,
    i18n,
    Trans,
    currentLang,
    onChangeLang: handleChangeLang,
    onResetLang: handleResetLang,
  };
}

export function useLocaleDirectionSync() {
  const { i18n, currentLang } = useTranslate();

  const handleSync = useCallback(async () => {
    // Only change language if we're in a browser environment and the language is actually different
    if (typeof window !== 'undefined' && i18n.resolvedLanguage !== currentLang.value) {
      await i18n.changeLanguage(currentLang.value);
    }
  }, [currentLang.value, i18n]);

  // Only run the effect once on mount, not on every render
  useEffect(() => {
    handleSync();
  }, []); // Empty dependency array to run only once

  return null;
}
